import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box,
    Button,
    RadioGroup,
    FormControlLabel,
    Radio,
    Typography,
} from '@mui/material';
import MaterialTable from 'material-table';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import Popup from "../../../Base/views/Popup";
import ConfirmationDialog from '../../../Base/views/ConfirmationDialog';
import OrgTypeConfigurePopup from './OrgTypePopup';
import {
    GetVMOrgTypeAction,
    UpdateVMOrgTypeAction,
    GetVMMultipleOrgConfigurationAction,
    DeleteVMMultipleOrgConfigurationAction,
    UpsertVMMultipleOrgConfigurationAction,
} from '../../../../../store/Employee/Visitors/Action';

const VisitorOrgType = () => {
    const dispatch = useDispatch();
    const {
        vmOrgTypeLoading,
        vmOrgTypeData,
        vmMultipleOrgLoading,
        vmMultipleOrgData,
    } = useSelector((state) => state?.EmployeeReducer?.VisitorsReducer);

    const [orgMode, setOrgMode] = useState('single');
    const [editedRecord, setEditedRecord] = useState(null);
    const [openPopup, setOpenPopup] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [currentData, setCurrentData] = useState({});

    useEffect(() => {
        dispatch(GetVMOrgTypeAction());
        dispatch(GetVMMultipleOrgConfigurationAction());
    }, [dispatch]);

    useEffect(() => {
        if (vmOrgTypeData && vmOrgTypeData.length > 0) {
            setOrgMode(vmOrgTypeData[0]?.is_vm_org_type_single ? 'single' : 'multiple');
        }
    }, [vmOrgTypeData]);

    const handleModeChange = (event) => {
        const newMode = event.target.value;
        dispatch(UpdateVMOrgTypeAction({ vm_org_type_single: newMode === 'single' }));
        setOrgMode(newMode);
    };

    const handleEdit = (rowData) => {
        setEditedRecord(rowData);
        setOpenPopup(true);
    };

    const handleConfirmDelete = (rowData) => {
        setOpenDialog(true);
        setCurrentData(rowData);
    };

    const handleAdd = () => {
        setEditedRecord(null);
        setOpenPopup(true);
    };

    const handleDelete = () => {
        dispatch(DeleteVMMultipleOrgConfigurationAction({
            multiple_org_configuration_pk: currentData?.multiple_org_configuration_pk
        }));
        setOpenDialog(false);
    };

    // ── Title bar: toggle on the left, Add button on the right (Multiple only) ──
    const tableTitle = (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pl: 1 }}>
            <RadioGroup
                row
                value={orgMode}
                onChange={handleModeChange}
                sx={{
                    '& .MuiRadio-root': {
                        color: '#8b44ff',
                        '&.Mui-checked': {
                            color: '#8b44ff',
                        },
                    },
                    '& .MuiFormControlLabel-label': {
                        fontSize: '13px',
                        fontWeight: 500,
                    },
                }}
            >
                <FormControlLabel value="single" control={<Radio size="small" />} label="Single" />
                <FormControlLabel value="multiple" control={<Radio size="small" />} label="Multiple" />
            </RadioGroup>

            {orgMode === 'multiple' && (
                <Button
                    onClick={handleAdd}
                    variant="contained"
                    className="mpl-primary-btn"
                    startIcon={<AddIcon />}
                    size="small"
                >
                    Add
                </Button>
            )}
        </Box>
    );

    return (
        <Box>
            <MaterialTable
                columns={orgMode === 'multiple' ? [
                    {
                        title: "Org Name",
                        field: "org_name",
                        cellStyle: { padding: "0px 10px 0px 10px" },
                    },
                    {
                        title: "Contact Person",
                        field: "contact_person_name",
                        cellStyle: { padding: "0px 10px 0px 10px" },
                    },
                    {
                        title: "Email",
                        field: "contact_email",
                        cellStyle: { padding: "0px 10px 0px 10px" },
                    },
                    {
                        title: "Mobile",
                        field: "contact_mobile_number",
                        cellStyle: { padding: "0px 10px 0px 10px" },
                        render: (rowData) => rowData?.contact_mobile_number || "-"
                    },
                    {
                        title: "Department",
                        field: "org_department",
                        cellStyle: { padding: "0px 10px 0px 10px" },
                        render: (rowData) => rowData?.org_department || "-"
                    },
                ] : []}
                data={orgMode === 'multiple' ? vmMultipleOrgData : []}
                isLoading={vmOrgTypeLoading || vmMultipleOrgLoading}
                options={{
                    maxBodyHeight: "250px",
                    // minBodyHeight: "250px",
                    rowStyle: { fontSize: "13px" },
                    headerStyle: { padding: "0px 10px 0px 10px", fontWeight: "bold" },
                    sorting: orgMode === 'multiple',
                    search: orgMode === 'multiple',
                    searchFieldAlignment: "right",
                    searchAutoFocus: false,
                    searchFieldVariant: "standard",
                    filtering: false,
                    paging: orgMode === 'multiple',
                    pageSizeOptions: [25, 50],
                    pageSize: 25,
                    paginationType: "stepped",
                    showFirstLastPageButtons: false,
                    paginationPosition: "bottom",
                    exportButton: false,
                    exportAllData: false,
                    addRowPosition: "first",
                    selection: false,
                    actionsColumnIndex: -1,
                    showSelectAllCheckbox: false,
                    showTextRowsSelected: false,
                    grouping: false,
                    columnsButton: false,
                }}
                actions={orgMode === 'multiple' ? [
                    (rowData) => ({
                        icon: () => (<EditIcon sx={{ color: "#637381" }} />),
                        tooltip: "Edit",
                        onClick: (_event, rowData) => handleEdit(rowData),
                    }),
                    (rowData) => ({
                        icon: () => (<DeleteIcon sx={{ color: "#637381" }} />),
                        tooltip: "Delete",
                        onClick: (_event, rowData) => handleConfirmDelete(rowData),
                    }),
                ] : []}
                localization={{
                    body: {
                        emptyDataSourceMessage: orgMode === 'single'
                            ? (
                                <Box sx={{ py: 3, textAlign: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Single organization mode — all visitors are handled under one company. No additional setup needed.
                                    </Typography>

                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        Switch to “Multiple” mode if your premises host more than one company.
                                    </Typography>
                                </Box>
                            )
                            : 'No companies added yet.',
                    },
                }}
                title={tableTitle}
            />

            {/* Add / Edit Popup */}
            <Popup
                title={editedRecord ? "Edit Organization" : "Add Organization"}
                openPopup={openPopup}
                setOpenPopup={setOpenPopup}
                maxWidth="md"
                fullWidth
            >
                <OrgTypeConfigurePopup
                    editedRecord={editedRecord}
                    setOpenPopup={setOpenPopup}
                    onSave={(data) => {
                        dispatch(UpsertVMMultipleOrgConfigurationAction(data));
                        setOpenPopup(false);
                    }}
                />
            </Popup>

            {/* Delete Confirmation */}
            <ConfirmationDialog
                title="Do you wish to proceed?"
                openDialog={openDialog}
                closeDialog={setOpenDialog}
                popupTitle="Organization Management"
                OkButtonText="Delete"
                onSubmit={handleDelete}
            />
        </Box>
    );
};

export default VisitorOrgType;
